import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { OfflineBootstrapCacheService } from './offline-bootstrap-cache.service';
import { isNetworkFailure, knownOffline, withTimeout } from './offline-network-failure';

const SESSION_TIMEOUT_MS = 8000;

export interface OfflineSessionInfo {
  isAdmin: boolean;
  offlineEnabled: boolean;
  entitlementTtlDays: number;
}

const CACHE_KIND = 'offlinesession';

// A SECOND, deliberately primitive copy of the same three values, in localStorage.
//
// The IndexedDB copy goes through OfflineBootstrapCacheService, which encrypts it with a
// CryptoKey that is itself stored in IndexedDB. That is a heavy mechanism for
// {isAdmin, offlineEnabled, entitlementTtlDays} — three non-sensitive settings the server
// hands out to any logged-in user of the school — and on WebKit (Safari, and every iOS
// browser including Chrome, which is WKWebView underneath) it is the fragile link:
// CryptoKey-in-IndexedDB has long-standing gaps there, and iOS clears script-writable
// storage after seven days without interaction, which Blink does not do.
//
// When that copy is missing offline, refresh() used to throw, app.component caught it and set
// offlineFeatureOn = false, and the student was shown the admin "check your internet
// connection" wall with the router outlet removed — no route to content already on their
// device. Losing an encrypted blob should never cost someone access to their downloads.
const SESSION_MIRROR_KEY = 'impata.offline.session';

function readMirror(): OfflineSessionInfo | null {
  try {
    const raw = localStorage.getItem(SESSION_MIRROR_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.offlineEnabled !== 'boolean' || typeof parsed?.isAdmin !== 'boolean') return null;
    return {
      isAdmin: parsed.isAdmin,
      offlineEnabled: parsed.offlineEnabled,
      entitlementTtlDays: typeof parsed.entitlementTtlDays === 'number' ? parsed.entitlementTtlDays : 7,
    };
  } catch {
    return null;
  }
}

function writeMirror(info: OfflineSessionInfo): void {
  try {
    localStorage.setItem(SESSION_MIRROR_KEY, JSON.stringify(info));
  } catch {
    /* private mode or quota — the IndexedDB copy is still there */
  }
}

// Caches GET /offline/session — a coarse, session-wide "is this user an admin at this
// school" signal plus the school-level toggle and entitlement TTL. Fetched once by
// app.component.ts's ngOnInit. Read synchronously elsewhere via `current`.
//
// Falls back to a locally-persisted copy on a network failure (status 0) — this response is
// what app.component.ts uses to decide banner-vs-blocking-overlay, so without a fallback, a
// reload while offline defaults isAdmin/offlineEnabled to false and shows the full blocking
// overlay instead of the banner, even for a student who has content saved and could otherwise
// see it. See offline-bootstrap-cache.service.ts for why this isn't gated by the feature flag.
@Injectable({
  providedIn: 'root',
})
export class OfflineSessionService {
  private info: OfflineSessionInfo | null = null;
  private fetchPromise: Promise<OfflineSessionInfo> | null = null;

  constructor(private http: HttpClient, private bootstrapCache: OfflineBootstrapCacheService) {}

  get current(): OfflineSessionInfo | null {
    return this.info;
  }

  refresh(): Promise<OfflineSessionInfo> {
    // Same reasoning as app.component.ts's loadSchoolInfoBundle: do not spend a gateway
    // timeout discovering something the browser already knows.
    if (knownOffline()) {
      return this.bootstrapCache
        .getCached<OfflineSessionInfo>(CACHE_KIND)
        .catch(() => null) // an unreadable store must fall through to the mirror, not reject
        .then((cached) => {
          const info = cached ?? readMirror();
          if (!info) throw new Error('offline and no cached session');
          this.info = info;
          return info;
        });
    }

    if (!this.fetchPromise) {
      this.fetchPromise = withTimeout(
        this.http.get<OfflineSessionInfo>(environment.ImpataAppAPIRoot + 'offline/session').toPromise(),
        SESSION_TIMEOUT_MS,
        'offline/session',
      )
        .then((info) => {
          this.info = info;
          writeMirror(info);
          this.bootstrapCache.cache(CACHE_KIND, info).catch((err) => console.error('[offline] failed to cache session info', err));
          return info;
        })
        .catch(async (err) => {
          this.fetchPromise = null;
          if (isNetworkFailure(err)) {
            const cached = await this.bootstrapCache.getCached<OfflineSessionInfo>(CACHE_KIND).catch(() => null);
            const info = cached ?? readMirror();
            if (info) {
              this.info = info;
              return info;
            }
          }
          throw err;
        });
    }
    return this.fetchPromise;
  }

  // Called on logout, alongside OfflineDbService.wipeAll() — stale admin/toggle state
  // must not survive into the next session on a shared device.
  reset(): void {
    this.info = null;
    this.fetchPromise = null;
    try {
      localStorage.removeItem(SESSION_MIRROR_KEY);
    } catch {
      /* nothing to do */
    }
  }
}
